import { useState } from "react"

function shortLinkLabel(url) {
  return url.replace(/^https?:\/\//, "")
}

export default function RecentlyClaimedSection({ codes, onDisclaim, onAcknowledgeAll }) {
  const [busyToken, setBusyToken] = useState(null)
  const [dismissing, setDismissing] = useState(false)

  if (!codes || codes.length === 0) return null

  async function handleDisclaim(token) {
    setBusyToken(token)
    try {
      await onDisclaim(token)
    } finally {
      setBusyToken(null)
    }
  }

  async function handleAckAll() {
    setDismissing(true)
    try {
      await onAcknowledgeAll()
    } finally {
      setDismissing(false)
    }
  }

  return (
    <section
      aria-labelledby="recently-claimed-heading"
      className="border border-voltage/40 bg-[color:var(--color-cloud)] dark:bg-coal rounded-lg mb-5"
    >
      <header className="flex items-start justify-between gap-4 px-4 py-3 border-b border-[color:var(--color-border)] dark:border-storm">
        <div>
          <h2 id="recently-claimed-heading" className="font-display font-semibold text-ink dark:text-cloud text-base">
            Recently saved to your account
          </h2>
          {/* Reason-agnostic on purpose: a code lands here either because this
              browser created it (the tq_codes creation proof) or because the
              email on the account matched, and a batch routinely mixes both.
              Per-code reasons would need a claim_reason column and would read
              as noise above a list that already offers "Not mine, remove". */}
          <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1 leading-relaxed">
            We saved {codes.length === 1 ? "this code" : `these ${codes.length} codes`} to your
            account — {codes.length === 1 ? "it was" : "they were"} created from this browser, or
            with your email address. Spot anything that isn't yours?
          </p>
        </div>
        <button
          type="button"
          onClick={handleAckAll}
          disabled={dismissing}
          className="bg-voltage text-coal rounded px-3 py-2 text-sm font-medium hover:bg-voltage-dark transition disabled:opacity-50 flex-shrink-0"
        >
          {dismissing ? "Dismissing…" : "Yes, all mine"}
        </button>
      </header>

      <ul className="divide-y divide-[color:var(--color-border)] dark:divide-storm">
        {codes.map((code) => (
          <li key={code.token} className="px-4 py-3 flex gap-3 items-center">
            <img
              src={code.image_url}
              alt=""
              aria-hidden="true"
              className="w-10 h-10 border border-[color:var(--color-border)] dark:border-storm rounded bg-white flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              {/* Truncated like the line below it: untruncated, a long link
                  ran under "Not mine, remove" on a phone. */}
              <div className="font-mono text-sm text-voltage truncate" title={code.short_url}>{shortLinkLabel(code.short_url)}</div>
              <div className="text-sm text-ink dark:text-cloud truncate" title={code.destination_url}>→ {code.destination_url}</div>
            </div>
            <button
              type="button"
              onClick={() => handleDisclaim(code.token)}
              disabled={busyToken === code.token}
              className="text-fail border border-fail rounded px-3 py-1 text-xs font-medium hover:bg-fail hover:text-white transition disabled:opacity-50 flex-shrink-0"
            >
              {busyToken === code.token ? "Removing…" : "Not mine, remove"}
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
